import { BodyText, Button, Progress, Separator } from "@/components/ui";
import type { UseFormReturn } from "react-hook-form";
import type { VisitClosureFormData, VisitClosureFormInput } from "./schemas";
import type { VisitClosureStepId } from "./steps";

type VisitClosureForm = UseFormReturn<
  VisitClosureFormInput,
  unknown,
  VisitClosureFormData
>;

const VISIT_CLOSURE_STEP_FORM_FIELD_NAMES = {
  billingCodes: "icd10",
  diagnosisCharting: null,
  dxBillingCodes: "dxBillingCodes",
  programEnrollment: "programEnrollment",
  visitTime: "encounterTimeData",
  cptCodes: "cptCode",
  billingSummary: null,
  carePlanPreview: null,
  rejectAsyncVisit: "rejectAsyncVisit",
} satisfies Record<VisitClosureStepId, keyof VisitClosureFormInput | null>;

interface VisitClosureFooterProps {
  form: VisitClosureForm;
  steps: readonly VisitClosureStepId[];
  step: number;
  setStep: (step: number) => void;
  isSubmitting: boolean;
  isDiagnosisLoading: boolean;
  selectableDiagnosisCount: number;
  isCarePlanPreviewReady: boolean;
}

export const VisitClosureFooter = ({
  form,
  steps,
  step,
  setStep,
  isSubmitting,
  isDiagnosisLoading,
  selectableDiagnosisCount,
  isCarePlanPreviewReady,
}: VisitClosureFooterProps) => {
  const currentStep = steps[step];
  const totalSteps = steps.length;
  const billingSummaryStepIndex = steps.indexOf("billingSummary");
  const formFieldName = VISIT_CLOSURE_STEP_FORM_FIELD_NAMES[currentStep];
  const dxBillingCodes = form.watch("dxBillingCodes") ?? [];
  const cancelReasonCode = form.watch("rejectAsyncVisit.cancelReasonCode");
  const selectedDiagnosisCount =
    form.watch("selectedDiagnosisIds")?.length ?? 0;
  const dxBillingCodesFieldError = form.formState.errors.dxBillingCodes;
  const dxBillingCodesError =
    dxBillingCodesFieldError?.message ??
    (dxBillingCodesFieldError ? "Review billing code errors" : undefined);

  const handleNext = async () => {
    if (currentStep === "billingSummary") {
      return;
    }

    if (formFieldName) {
      const isValid = await form.trigger(formFieldName);
      if (!isValid) {
        return;
      }
    }

    setStep(step + 1);
  };

  const errors = form.formState.errors;
  const hasCurrentStepErrors = formFieldName
    ? Boolean(errors[formFieldName])
    : currentStep === "billingSummary"
      ? Object.keys(errors).length > 0
      : false;

  const isNextDisabled =
    currentStep === "diagnosisCharting"
      ? isDiagnosisLoading
      : currentStep === "dxBillingCodes"
        ? dxBillingCodes.length < 1 || Boolean(dxBillingCodesFieldError)
        : currentStep === "rejectAsyncVisit"
          ? !cancelReasonCode || hasCurrentStepErrors
          : currentStep === "carePlanPreview"
            ? !isCarePlanPreviewReady
            : hasCurrentStepErrors;

  return (
    <>
      <Separator />
      <div className="flex min-h-8 flex-col gap-[10px] sm:justify-between">
        {totalSteps > 1 && (
          <div className="flex items-center gap-3 sm:justify-between">
            <BodyText size="sm" textColor="tertiary" className="flex">
              Step {step + 1} of {totalSteps}
            </BodyText>
            <Progress
              value={((step + 1) / totalSteps) * 100}
              max={100}
              className="flex flex-1"
            />
          </div>
        )}
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          {currentStep === "diagnosisCharting" ? (
            isDiagnosisLoading ? (
              <BodyText size="sm" textColor="tertiary">
                Loading diagnoses...
              </BodyText>
            ) : (
              <BodyText
                size="sm"
                textColor={selectedDiagnosisCount > 0 ? "success" : "tertiary"}
              >
                {selectedDiagnosisCount === 0
                  ? "No diagnoses selected. Nothing will be added to the medical record."
                  : `${selectedDiagnosisCount} of ${selectableDiagnosisCount} new ${selectableDiagnosisCount === 1 ? "diagnosis" : "diagnoses"} will be added to the medical record.`}
              </BodyText>
            )
          ) : (
            <BodyText
              size="sm"
              textColor={dxBillingCodesError ? "error" : "tertiary"}
              className={
                currentStep === "dxBillingCodes" ? "flex" : "invisible"
              }
            >
              {dxBillingCodesError ??
                (dxBillingCodes.length < 1
                  ? "At least one billing code is required"
                  : "")}
            </BodyText>
          )}
          <div className="flex flex-row justify-end gap-2">
            {totalSteps > 1 && (
              <Button
                variant="secondary"
                type="button"
                disabled={step === 0}
                onClick={() => setStep(step - 1)}
              >
                Previous
              </Button>
            )}
            {step === billingSummaryStepIndex ||
            currentStep === "rejectAsyncVisit" ||
            currentStep === "carePlanPreview" ? (
              <Button
                key="submit"
                variant="primary"
                type="submit"
                isLoading={isSubmitting}
                disabled={isNextDisabled || isSubmitting}
              >
                Submit
              </Button>
            ) : (
              <Button
                key="next"
                variant="primary"
                type="button"
                onClick={handleNext}
                disabled={isNextDisabled}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
